import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import Container from '../../common/components/Container/Container';
import TitleContainer from '../../common/components/TitleContainer/TitleContainer';
import { Routes } from '../../config/routes';
import { FlowConfigContext } from '../../providers/FlowConfigContext';

enum ConfigErrorType {
  UnsupportedVersion = 'unsupportedVersion',
  CivicSignsNotSupported = 'civicSignsNotSupported',
  InvalidConfiguration = 'invalidConfiguration',
}

function InvalidConfigErrorScreen({ errorType, testId }: { errorType: ConfigErrorType; testId: string }): JSX.Element {
  const { getExternalLinksConfig } = useContext(FlowConfigContext);
  const { civicSupportLink } = getExternalLinksConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const handleContactSupport = () => {
    navigate(Routes.SUPPORT, { state: { from: location.pathname } });
  };

  return (
    <Container>
      <TitleContainer center>
        <h1 className="mb-2" data-testid={testId}>
          {t(`screens.${errorType}.title`)}
        </h1>
        <span className="mb-4 p pass-status-text" data-testid={`screens.${errorType}.description`}>
          {t(`screens.${errorType}.description`)}
        </span>
        <span className="mb-4 p pass-status-text" data-testid={`screens.${errorType}.subDescription`}>
          {t(`screens.${errorType}.subDescription`)}
        </span>
        <a
          data-testid="COMPONENT_HYPERLINK"
          className="mb-4 textLink"
          href="https://www.npmjs.com/package/@civic/solana-gateway-react"
          target="_blank"
          rel="noreferrer"
        >
          {t(`screens.${errorType}.reactComponentLink`)}
        </a>
      </TitleContainer>
      <span className="w-full whitespace-pre-wrap textDetail text-center mt-6">
        {t('common.support.supportLinkQuestions')}
        <a target="_blank" title="Help Center" href={civicSupportLink} className="textLinkDetail" rel="noreferrer">
          {t('common.support.supportLinkVisitHelpCenter')}
        </a>
        {t('common.support.supportLinkOr')}
        <button
          type="button"
          className="buttonLink"
          title="Contact support"
          onClick={handleContactSupport}
          data-testid="CONTACT_SUPPORT_BUTTON"
        >
          {t('common.support.supportLinkContactSupport')}
        </button>
      </span>
    </Container>
  );
}

export const UnsupportedVersion = (): JSX.Element =>
  InvalidConfigErrorScreen({
    errorType: ConfigErrorType.UnsupportedVersion,
    testId: 'UNSUPPORTED_VERSION_ERROR_SCREEN',
  });

export const CivicSignsNotSupported = (): JSX.Element =>
  InvalidConfigErrorScreen({
    errorType: ConfigErrorType.CivicSignsNotSupported,
    testId: 'CIVIC_SIGNS_NOT_SUPPORTED_ERROR_SCREEN',
  });

  export const InvalidConfiguration = (): JSX.Element =>
  InvalidConfigErrorScreen({
    errorType: ConfigErrorType.InvalidConfiguration,
    testId: 'INVALID_CONFIGURATION_ERROR_SCREEN',
  });
